import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// ── Draft journal entries ───────────────────────────────────────────────────────────────────────
// Entries derived from the VAT sub-ledger, waiting for a person. Nothing reaches the statutory
// books from here without someone posting it, which is the point: the machine can tell what a
// document is, but not always which expense account it belongs to, and a guess that posts itself is
// worse than a blank waiting to be filled.
//
// A purchase draft arrives on the account recorded for that SUPPLIER (services/vendorAccounts.ts,
// answered once on the Supplier → account panel). Where no answer exists yet it still parks on 5900
// "other expenses" and says so on the draft, because a guess that posts itself is worse than a
// blank waiting to be filled — picking the right account IS the review.

type Line = { id: string; line_no: number; account_id: string; dr: number; cr: number; code: string; name_th: string; name_en: string; partner_id: string };
type Draft = { id: string; entry_date: string; source_book: string; doc_no: string; narration: string; calc_note: string; lines: Line[] };
type Account = { id: string; code: string; name_th: string; name_en: string; class: string };

// The short statutory journal names (เงินสด / รายวันซื้อ …) live in the translation table so the
// label survives a locale switch unchanged — they are the names the server prints, not English that
// happens to be Thai. Keys only here: t() is a hook and cannot be called at module scope.
const BOOK_KEY: Record<string, string> = { cash: 'pa_jnl_cash', bank: 'pa_jnl_bank', purchase: 'pa_jnl_purchase', sale: 'pa_jnl_sale', general: 'pa_jnl_general' };
const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
const baht = (satang: number) => (Number(satang || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const btn: React.CSSProperties = {
  padding: '7px 12px', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};

export default function DraftEntries({ clientId, periodId, periodClosed, onPosted, refreshKey = 0 }:
  { clientId: string; periodId: string; periodClosed: boolean; onPosted: () => void; refreshKey?: number }) {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState<Draft[] | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  // refreshKey is bumped by the panel above when it creates a draft — an adjustment, a reversal or
  // a swept-in accrual. Without it a new draft sits on the server, invisible until the page reloads.
  const load = useCallback(async () => {
    try {
      const d: any = await api.jnlDrafts(clientId, periodId);
      setDrafts(d.entries || []);
    } catch (e: any) { setErr(e.message); setDrafts([]); }
  }, [clientId, periodId, refreshKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setOpen(false); setMsg(''); setErr(''); load(); }, [load]);
  useEffect(() => { api.jnlAccounts(clientId).then((d: any) => setAccounts(d.accounts || [])).catch(() => setAccounts([])); }, [clientId]);

  const generate = async () => {
    setBusy('gen'); setErr(''); setMsg('');
    try {
      const r: any = await api.jnlGenerate(clientId, periodId);
      // Counted, not inflected: zh and th have no plural, so each count carries its own label.
      const bits = [t('pa_draft_gen_created', { n: r.created })];
      if (r.skipped) bits.push(t('pa_draft_gen_skipped', { n: r.skipped }));
      if (r.needsAccount) bits.push(t('pa_draft_gen_needs_account', { n: r.needsAccount }));
      setMsg(bits.join(' · '));
      // Refusals are listed rather than counted: a line the generator would not touch is the one
      // thing a person actually has to go and look at. b.why is the server's own words — unwrapped.
      if (r.blocked?.length) {
        const list = r.blocked.slice(0, 4).map((b: any) => `${b.docNo || t('pa_draft_no_doc_no')}: ${b.why}`).join('; ') +
          (r.blocked.length > 4 ? t('pa_draft_gen_blocked_more', { n: r.blocked.length - 4 }) : '');
        setErr(t('pa_draft_gen_blocked', { n: r.blocked.length, list }));
      }
      if (r.created) setOpen(true);
      await load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const setAccount = async (lineId: string, accountId: string) => {
    setErr('');
    try { await api.jnlSetLineAccount(lineId, accountId); await load(); }
    catch (e: any) { setErr(e.message); }
  };

  const post = async (ids: string[], label: string) => {
    setBusy(label); setErr(''); setMsg('');
    try {
      const r: any = ids.length === 1 ? await api.jnlPost(ids[0]) : await api.jnlPostMany(ids);
      if (r.failed?.length) setErr(r.failed.map((f: any) => f.why).join('; '));
      else setMsg(ids.length === 1 ? t('pa_draft_posted_single') : t('pa_draft_posted_n', { n: r.posted }));
      await load(); onPosted();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const discard = async (id: string, docNo: string) => {
    if (!window.confirm(t('pa_draft_discard_confirm', { docNo }))) return;
    setBusy(id); setErr('');
    try { await api.jnlDiscard(id); await load(); } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  if (drafts === null) return null;
  const n = drafts.length;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', flexWrap: 'wrap' }}>
        <i className="ti ti-file-pencil" style={{ fontSize: 18, color: n ? '#B45309' : 'var(--text-tertiary)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
            {t('pa_draft_title')}
            {n > 0 && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 800, color: '#92400E', background: '#FFFBEB', border: '1px solid #F1C232', padding: '1px 8px', borderRadius: 999 }}>{n}</span>}
          </span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>
            {n ? t('pa_draft_sub_pending') : t('pa_draft_sub_empty')}
          </span>
        </span>
        {!periodClosed && (
          <button onClick={generate} disabled={!!busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}>
            <i className={busy === 'gen' ? 'ti ti-loader' : 'ti ti-wand'} style={{ display: 'inline-block', animation: busy === 'gen' ? 'spin 0.7s linear infinite' : undefined }} aria-hidden />
            {' '}{t('pa_draft_generate')}
          </button>
        )}
        {n > 0 && <button onClick={() => setOpen(o => !o)} style={btn}>{open ? t('pa_draft_hide') : t('pa_draft_review')}</button>}
      </div>

      {msg && <div style={{ fontSize: 12.5, color: '#166534', background: '#E8F6EE', borderTop: '1px solid #BBE5CB', padding: '8px 13px' }}>{msg}</div>}
      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', borderTop: '1px solid #F3B9B9', padding: '8px 13px' }}>{err}</div>}

      {open && n > 0 && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 13px 13px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button onClick={() => post(drafts.map(d => d.id), 'all')} disabled={!!busy || periodClosed}
              style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none', opacity: busy ? 0.6 : 1 }}>
              <i className={busy === 'all' ? 'ti ti-loader' : 'ti ti-check'} aria-hidden /> {t('pa_draft_post_all', { n })}
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {drafts.map(d => (
              <div key={d.id} style={{ border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 11px', background: 'var(--bg-app)', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-primary)' }}>{d.doc_no}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{dmy(d.entry_date)} · {t(BOOK_KEY[d.source_book] || d.source_book)}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.narration}</span>
                  <button onClick={() => post([d.id], d.id)} disabled={!!busy || periodClosed} style={{ ...btn, padding: '5px 10px', fontSize: 11.5 }}>{t('pa_draft_post')}</button>
                  <button onClick={() => discard(d.id, d.doc_no)} disabled={!!busy} title={t('pa_draft_discard_title')}
                    style={{ ...btn, padding: '5px 9px', fontSize: 11.5, color: '#B42318' }}><i className="ti ti-trash" aria-hidden /></button>
                </div>
                {d.calc_note && (
                  <div style={{ fontSize: 11.5, color: '#92400E', background: '#FFFBEB', padding: '6px 11px', borderBottom: '1px solid var(--border)' }}>
                    <i className="ti ti-info-circle" aria-hidden /> {d.calc_note}
                  </div>
                )}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <tbody>
                    {d.lines.map(l => (
                      <tr key={l.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 11px', width: '55%' }}>
                          {/* The account is editable on a draft and only on a draft — a posted entry
                              is corrected by reversing it, never by quietly changing what it said. */}
                          <select value={l.account_id} onChange={e => setAccount(l.id, e.target.value)} disabled={periodClosed}
                            style={{ width: '100%', maxWidth: 380, padding: '4px 7px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}>
                            {accounts.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name_th} — {a.name_en}</option>)}
                          </select>
                          {l.partner_id && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-tertiary)' }}>{l.partner_id}</span>}
                        </td>
                        <td style={{ padding: '6px 11px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{l.dr ? baht(l.dr) : ''}</td>
                        <td style={{ padding: '6px 11px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{l.cr ? baht(l.cr) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
